import api from './config';
import { trackEvent } from '@/utils/analytics';

export interface ReactionResult {
  userReaction: string | null;
  counts: Record<string, number>;
}

/**
 * Toggle the current user's emoji reaction on a recommendation.
 * Returns the fresh aggregate ({ userReaction, counts }).
 */
export const reactToRecommendation = async (
  recommendationDocumentId: string,
  emoji: string,
): Promise<ReactionResult> => {
  const response = await api.post(`/recommendation/${recommendationDocumentId}/react`, { emoji });
  const result = response.data as ReactionResult;
  trackEvent('Reaction', result.userReaction === emoji ? 'add' : 'remove', emoji); // a toggle
  return result;
};

/**
 * reaction-emoji-signal — the qualified catalogue, from the server.
 *
 * Returns `null` on any failure: the embedded copy in
 * `@/constants/reactionCatalogue` then stands. A picker that blanks because the
 * network blinked is a worse regression than a slightly stale list.
 */
export interface ReactionCatalogueGroup {
  family: string;
  emoji: string;
  negative: boolean;
  emojis: string[];
}

export async function fetchReactionCatalogue(): Promise<ReactionCatalogueGroup[] | null> {
  try {
    const res = await api.get('/reactions');
    const data = res.data?.data;
    if (!Array.isArray(data)) return null;
    return data.map(
      (g: {
        family: string;
        emoji?: string;
        negative?: boolean;
        emojis?: { value: string }[];
      }) => ({
        family: String(g.family),
        emoji: String(g.emoji ?? ''),
        negative: Boolean(g.negative),
        emojis: (g.emojis ?? []).map((e) => String(e.value)),
      }),
    );
  } catch {
    return null;
  }
}
